import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import { NPIResult } from "@/src/lib/npi";
import { customCapitalCase } from "@/src/lib/string";
import { Practitioner, practitionerShape } from "@/src/models/practitioner";

export const basicSchema = z.object({
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  middle_name: z.string().optional(),
  organization_name: z.string().optional(),
  credential: z.string().optional(),
  sole_proprietor: z.enum(["YES", "NO"]).optional(),
  gender: z.string().optional(),
  name_prefix: z.string().optional(),
  name_suffix: z.string().optional(),
  enumeration_date: z.string(), // ISO 8601
  last_updated: z.string(), // ISO 8601
  date_of_birth: z.string(), // ISO 8601
  status: z.enum(["A", "I"]), // Active/Inactive
});

export const taxonomySchema = z.object({
  code: z.string(),
  desc: z.string(),
  primary: z.boolean(),
  state: z.string().optional(),
  license: z.string().optional(),
});

export const addressSchema = z.object({
  address_purpose: z.enum(["LOCATION", "MAILING"]),
  address_type: z.enum(["DOM", "PHYS"]),
  address_1: z.string(),
  address_2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  postal_code: z.string(),
  country_code: z.string(),
  telephone_number: z.string().optional(),
  fax_number: z.string().optional(),
});

export const identifiersSchema = z.object({
  identifier: z.string(),
  code: z.string(),
  desc: z.string(),
  state: z.string().optional(),
  issuer: z.string().optional(),
});

export const resultSchema = z.object({
  number: z.string(), // NPI
  enumeration_type: z.enum(["NPI-1", "NPI-2"]),
  basic: basicSchema,
  addresses: z.array(addressSchema),
  taxonomies: z.array(taxonomySchema),
  identifiers: z.array(identifiersSchema).optional(),
  other_names: z
    .array(
      z.object({
        type: z.string(),
        organization_name: z.string().optional(),
        first_name: z.string().optional(),
        last_name: z.string().optional(),
      })
    )
    .optional(),
});

// TODO: Uncomment these when we use them. Duplicate NPIResult type from npi.ts

// export const npiResultSchema = z.object({
//   result_count: z.number(),
//   results: z.array(resultSchema),
// });

// export type NPIResult = z.infer<typeof npiResultSchema>;

export const convertNPIResultToProvider = (result: NPIResult): Practitioner => {
  const primarySpecialty = result.taxonomies.find((taxonomy) => taxonomy.primary)?.desc;
  const now = new Date().toISOString();

  return {
    ...practitionerShape,
    id: uuidv4(),
    givenName: customCapitalCase(result.basic.first_name),
    familyName: customCapitalCase(result.basic.last_name),
    middleName: customCapitalCase(result.basic.middle_name),
    specialty: primarySpecialty || null,
    npiNumber: result.number,
    createdAt: now,
    updatedAt: now,
    prefix: result.basic.name_prefix ? [result.basic.name_prefix] : [],
    suffix: result.basic.name_suffix ? [result.basic.name_suffix] : [],
    npiRegistryEntry: result,
  };
};
